import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, X, MapPin, Search, Loader2, Plus } from "lucide-react";
import { useLocationFilter } from "../../context/LocationContext";
import { useFx } from "../../context/FxContext";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";
import { CURRENCIES, detectHomeCurrency } from "../../utils/currency";
import api from "../../api";

export const useTouristMode = () => {
  const { t } = useLang();
  const { requestLocation, setTourist } = useLocationFilter();
  const { displayCurrency, setDisplayCurrency } = useFx();
  const { toast } = useToast();
  const navigate = useNavigate();

  const enable = async () => {
    if (!displayCurrency) setDisplayCurrency(detectHomeCurrency());
    setTourist(true);
    const ok = await requestLocation();
    if (ok) {
      toast({ title: t("tm.touristModeOn"), description: t("tm.showingDealsNearYou") });
    } else {
      toast({ title: t("tm.touristModeOn"), description: t("tm.typeTheHotelLandmark") });
    }
    navigate("/browse");
  };

  return { enable };
};

export const TouristButton = ({ variant = "header" }) => {
  const { tourist } = useLocationFilter();
  const { enable } = useTouristMode();
  const { t } = useLang();

  if (variant === "home") {
    return (
      <div className="rounded-2xl border border-[#16a34a]/30 bg-[#e7f6ec] dark:bg-[#16341f] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="tourist-home-card">
        <span className="h-12 w-12 rounded-xl bg-[#16a34a] text-white flex items-center justify-center shrink-0"><Plane size={22} /></span>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-foreground">{t("tourist.visitingTitle")}</h3>
          <p className="text-sm text-muted-foreground">{t("tourist.visitingDesc")}</p>
        </div>
        <button onClick={enable} data-testid="tourist-home-btn" className="shrink-0 inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold px-5 py-2.5 rounded-xl transition-colors">
          <Plane size={16} /> {t("tourist.imVisiting")}
        </button>
      </div>
    );
  }

  if (variant === "sheet") {
    return (
      <button type="button" onClick={enable} data-testid="tourist-mode-btn-sheet"
        className={`w-full h-11 px-3 rounded-xl flex items-center justify-between text-sm font-semibold transition-colors ${tourist ? "bg-[#16a34a] text-white" : "bg-secondary text-foreground hover:bg-[#e7f6ec] dark:hover:bg-[#16341f]"}`}>
        {t("tourist.imVisiting")} <Plane size={17} className={tourist ? "" : "text-[#16a34a]"} />
      </button>
    );
  }

  return (
    <button type="button" onClick={enable} data-testid="tourist-mode-btn" title={t("tm.iMVisitingShow")}
      className={`shrink-0 h-11 flex items-center gap-1.5 px-3 rounded-xl border text-sm font-semibold transition-colors ${tourist ? "bg-[#16a34a] text-white border-[#16a34a]" : "bg-card border-border text-[#16a34a] hover:bg-[#e7f6ec] dark:hover:bg-[#16341f]"}`}>
      <Plane size={18} /> <span className="hidden xl:inline">{t("tourist.imVisiting")}</span>
    </button>
  );
};

export const TouristBanner = () => {
  const { t } = useLang();
  const { tourist, setTourist, place, gps, unit, toggleUnit, setGps, setPlace, savedBases, addBase, removeBase } = useLocationFilter();
  const { displayCurrency, setDisplayCurrency } = useFx();
  const { toast } = useToast();
  const [editing, setEditing] = useState(!gps);
  const [baseQ, setBaseQ] = useState("");
  const [loading, setLoading] = useState(false);
  if (!tourist) return null;

  const submitBase = async (e) => {
    if (e) e.preventDefault();
    if (!baseQ.trim()) return;
    setLoading(true);
    try {
      const r = await api.get("/geocode", { params: { q: baseQ } });
      setGps({ lat: r.data.lat, lng: r.data.lng });
      setPlace(r.data.formatted || baseQ);
      toast({ title: t("tm.baseUpdated"), description: t("tm.sortingAround", { place: r.data.formatted || baseQ }) });
      setEditing(false);
      setBaseQ("");
    } catch {
      toast({ title: t("tm.placeNotFound"), description: t("tm.tryAHotelName") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 lg:px-6 pt-3">
      <div className="flex flex-col gap-2 rounded-xl border border-[#16a34a]/30 bg-[#e7f6ec] dark:bg-[#16341f] px-4 py-2.5" data-testid="tourist-banner">
      <div className="flex flex-wrap items-center gap-3">
      <span className="flex items-center gap-2 text-sm font-bold text-[#0e6b3e] dark:text-[#7ee0a3]"><Plane size={16} /> {t("tm.touristMode")}</span>
      {editing ? (
        <form onSubmit={submitBase} className="flex items-center gap-1.5 bg-card border border-border rounded-lg px-2 py-1" data-testid="tourist-base-form">
          <MapPin size={12} className="text-[#16a34a] shrink-0" />
          <input autoFocus value={baseQ} onChange={(e) => setBaseQ(e.target.value)} placeholder={t("tm.hotelLandmarkOrAddress")} data-testid="tourist-base-input"
            className="bg-transparent outline-none text-xs w-44 text-foreground placeholder:text-muted-foreground" />
          <button type="submit" data-testid="tourist-base-go" className="text-[#16a34a] hover:text-[#15803d]" aria-label={t("tm.setBaseLocation")}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
          </button>
          <button type="button" onClick={() => { setEditing(false); setBaseQ(""); }} className="text-muted-foreground hover:text-foreground" aria-label={t("tm.cancel")}><X size={12} /></button>
        </form>
      ) : (
        <button onClick={() => setEditing(true)} data-testid="tourist-base-btn" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <MapPin size={12} className="text-[#16a34a]" /> {t("tm.nearPlace", { place: place || t("tm.you") })} <span className="text-[#16a34a] font-semibold ml-0.5">· {t("tm.changeBase")}</span>
        </button>
      )}
      <div className="flex items-center gap-2 ml-auto">
        <select value={displayCurrency || ""} onChange={(e) => setDisplayCurrency(e.target.value)} data-testid="tourist-currency-select"
          className="text-xs font-semibold bg-card border border-border rounded-lg px-2 py-1.5 text-foreground outline-none">
          <option value="">{t("tm.localPrices")}</option>
          {CURRENCIES.map((c) => (<option key={c.code} value={c.code}>{c.code}</option>))}
        </select>
        <div className="flex items-center bg-card rounded-lg p-0.5 border border-border">
          {["km", "mi"].map((u) => (
            <button key={u} onClick={() => { if (unit !== u) toggleUnit(); }} data-testid={`tourist-unit-${u}`}
              className={`px-2 py-1 text-xs font-bold rounded-md transition-colors ${unit === u ? "bg-[#16a34a] text-white" : "text-muted-foreground"}`}>{u}</button>
          ))}
        </div>
        <button onClick={() => setTourist(false)} data-testid="tourist-exit-btn" aria-label={t("tm.exitTouristMode")}
          className="h-7 w-7 rounded-lg bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"><X size={14} /></button>
      </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="tourist-bases">
        <span className="text-xs font-semibold text-muted-foreground">{t("tm.yourBases")}</span>
        {savedBases.map((b) => {
          const active = gps && Math.abs(gps.lat - b.lat) < 1e-4 && Math.abs(gps.lng - b.lng) < 1e-4;
          return (
            <span key={b.id} data-testid={`base-chip-${b.id}`}
              className={`inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[#16a34a] text-white border-[#16a34a]" : "bg-card border-border text-foreground hover:border-[#16a34a]"}`}>
              <button onClick={() => { setGps({ lat: b.lat, lng: b.lng }); setPlace(b.place); toast({ title: t("tm.switchedTo", { label: b.label }), description: t("tm.sortingAround", { place: b.place }) }); }}
                data-testid={`base-switch-${b.id}`} className="inline-flex items-center gap-1">
                <MapPin size={11} /> {b.label}
              </button>
              <button onClick={() => removeBase(b.id)} data-testid={`base-remove-${b.id}`} aria-label={t("tm.removeBase")}
                className={`h-4 w-4 rounded-full flex items-center justify-center ${active ? "hover:bg-white/20" : "hover:bg-secondary"}`}><X size={11} /></button>
            </span>
          );
        })}
        {savedBases.length === 0 && <span className="text-xs text-muted-foreground">{t("tm.noneYet")}</span>}
        <button onClick={() => addBase({ place, lat: gps?.lat, lng: gps?.lng })} disabled={!gps} data-testid="base-save-btn"
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-card border border-dashed border-[#16a34a]/60 text-[#16a34a] hover:bg-[#16a34a] hover:text-white transition-colors disabled:opacity-50">
          <Plus size={12} /> {t("tm.saveThisBase")}
        </button>
      </div>
    </div>
    </div>
  );
};
